import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { fileValidatorFn, FileUploadSettings, FileUploadSettingsStorage, UploadFile, UserStorage } from '@scisys-uim/uim-common';
import { StorageLocationDocumentInfo, StorageLocationDocumentUploadRestrictions, FirearmStorageLocationPLIService } from 'assets-service';
import { combineLatest, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-firearm-storage-location-pli-document',
  templateUrl: './firearm-storage-location-pli-document.component.html',
  styleUrls: ['./firearm-storage-location-pli-document.component.scss']
})
export class FirearmStorageLocationPLIDocumentComponent implements OnChanges, OnDestroy {
  @Input() pliId: number;
  @Output() documentsChanged = new EventEmitter<StorageLocationDocumentInfo[]>();
  documents: StorageLocationDocumentInfo[] = [];
  files: UploadFile[] = [];
  loaded = false;
  busy = false;
  error = '';
  private requests = new Subscription();
  uploadSettings: FileUploadSettings;
  uploadRestrictions: StorageLocationDocumentUploadRestrictions;
  fileValidator = fileValidatorFn([], undefined, [], [], [], []);

  constructor(private service: FirearmStorageLocationPLIService, private userStorage: UserStorage,
    private uploadSettingsStorage: FileUploadSettingsStorage) {}

  get canRead(): boolean { return this.userStorage.currentUserHasAnyAuthority('READ_FIREARM_STORAGE_LOCATION_PLI'); }
  get canWrite(): boolean { return this.userStorage.currentUserHasAnyAuthority('WRITE_FIREARM_STORAGE_LOCATION_PLI'); }
  get canUpload(): boolean {
    return !!this.pliId && !!this.uploadSettings && !!this.uploadRestrictions && this.canRead && this.canWrite && this.loaded && !this.busy &&
      !this.documents.length && this.files.length === 1 && !this.fileValidator(this.files[0].file);
  }

  ngOnChanges(): void {
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.documents = [];
    this.files = [];
    this.loaded = false;
    this.busy = false;
    this.error = '';
    this.uploadSettings = null;
    this.uploadRestrictions = null;
    this.refresh();
    this.requests.add(combineLatest([
      this.uploadSettingsStorage.resolve(), this.service.documentUploadRestrictions()
    ]).subscribe(([settings, restrictions]) => {
      this.uploadSettings = settings;
      this.uploadRestrictions = restrictions;
      this.fileValidator = fileValidatorFn([], settings?.maxFileSize,
        restrictions.whitelistedContentTypes, restrictions.whitelistedExtensions,
        restrictions.blacklistedContentTypes, restrictions.blacklistedExtensions);
    }, () => this.error = 'Unable to load upload settings. Reload the page before uploading.'));
  }

  ngOnDestroy(): void { this.requests.unsubscribe(); }

  private received(documents: StorageLocationDocumentInfo[]): void {
    this.documents = documents;
    this.loaded = true;
    this.documentsChanged.emit(documents);
  }

  refresh(preserveError = false): void {
    if (!this.pliId || !this.canRead || this.busy) { return; }
    if (!preserveError) { this.error = ''; }
    this.files = [];
    this.busy = true;
    this.requests.add(this.service.listDocuments(this.pliId).pipe(finalize(() => this.busy = false)).subscribe(
      documents => this.received(documents),
      () => {
        this.loaded = false;
        this.error = 'Unable to load the document. Please refresh before trying again.';
      }));
  }

  filesChanged(files: UploadFile[]): void { this.files = files || []; }

  upload(): void {
    if (!this.canUpload) { return; }
    const file = this.files[0].file;
    this.error = '';
    this.busy = true;
    this.requests.add(this.service.uploadDocument(this.pliId, file).pipe(finalize(() => this.busy = false)).subscribe(
      documents => { this.files = []; this.received(documents); },
      () => {
        this.error = 'Upload failed. Check the allowed file types and size; only one document is permitted. Refresh to check the saved document before retrying.';
        this.files = [];
        this.loaded = false;
      }));
  }

  deleteDocument(info: StorageLocationDocumentInfo): void {
    if (!this.canWrite || this.busy || !window.confirm(`Delete ${info.filename}?`)) { return; }
    this.error = '';
    this.busy = true;
    this.requests.add(this.service.deleteDocument(this.pliId, info.filename).pipe(finalize(() => this.busy = false)).subscribe(
      documents => this.received(documents),
      () => {
        this.loaded = false;
        this.error = 'Delete failed. Refresh to check the saved document before retrying.';
      }));
  }

  download(info: StorageLocationDocumentInfo): void {
    if (!this.canRead || this.busy) { return; }
    this.error = '';
    this.busy = true;
    this.requests.add(this.service.downloadDocument(this.pliId, info.filename).pipe(finalize(() => this.busy = false)).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = info.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }, () => this.error = 'Download failed. Please refresh and try again.'));
  }
}
